import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Grid, Typography } from "@mui/material";

import { useGiftsGiven, useGlobalGifts } from "JS/React/Hooks/Gifts";
import { useGlobalCourses } from "JS/React/Hooks/Course/Course";
import { AppHeader } from "JS/React/Components/AppHeader";
import {
    AppContentListItem,
    AppContentListItemType,
} from "JS/React/Components/AppContentListItem";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { AllTabs } from "JS/Models";
import { paginateData, PaginationInfo } from "JS/Types/Pagination";
import { fetchBatchThumbnailUrls } from "JS/React/Hooks/Common/ImageUpload";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useRouting } from "JS/React/Hooks/Routes";
import { sortData } from "JS/Types";
import { messaging } from "JS/Helpers/UserMessaging";
import { Layout } from "../Layout";
import qs from "qs";

const RootDiv = styled("div")(() => ({}));

const WrapperLoaderGrid = styled(Grid)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: `calc(100vh - ${theme.footer.height})`,
}));

const NoContentTypography = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    color: `${theme.palette.common.black}`,
}));

export const GiftsGivenContentListing = (props: any) => {
    const params: any = useParams();
    const { memberId, selectedTab, categoryId } = params;
    const Tabs: any = AllTabs;
    const { linkProvider } = useRouting();
    const { className, ...rest } = props;
    const {
        homeStack,
        giftsStack,
        setGlobalGiftsCategoryStack,
        resetGlobalGiftsCategoryStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        giftsStack[categoryId]?.searchQuery
            ? giftsStack[categoryId]?.searchQuery
            : "",
    );
    const { loading, loaded, filteredGifts } = useGiftsGiven(
        "gifts",
        searchQuery,
        memberId,
    );
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
        currentPage: 1,
        perPage: 10,
        total: 0,
    });
    const [thumbnails, setThumbnails] = useState<{
        [key: string]: string;
    }>(null);
    const navigate = useNavigate();
    const { scrollTo } = useQueryParams();
    const filteredByContentType = useMemo(
        () =>
            filteredGifts.filter((g) => {
                return (
                    (selectedTab === AllTabs.AUDIOS &&
                        g.content_type.includes("MP3")) ||
                    (selectedTab === AllTabs.VIDEOS &&
                        g.content_type.includes("Video")) ||
                    (selectedTab === AllTabs.COURSES &&
                        g.content_type.includes("Course"))
                );
            }),
        [filteredGifts, selectedTab],
    );

    const finalData = useMemo(
        () =>
            paginateData(
                sortData(filteredByContentType, [
                    {
                        col: "title",
                        dir: "asc",
                        position: 0,
                    },
                ]),
                paginationInfo,
            ).data,
        [filteredByContentType, paginationInfo],
    );

    useEffect(() => {
        setPaginationInfo({
            ...paginationInfo,
            total: filteredGifts ? filteredGifts.length : 0,
        });
        setGlobalGiftsCategoryStack(
            {
                searchQuery: searchQuery,
            },
            categoryId,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredGifts]);

    useEffect(() => {
        if (finalData) {
            fetchThumbnails();
        }
    }, [finalData]);

    const fetchThumbnails = async () => {
        const itemsToFetch = finalData
            .filter((d) => !thumbnails || !thumbnails[d.nid])
            .map((gift: any) => {
                const isGiftCourse =
                    gift.content_item_type_name === "GiftCourse";
                return {
                    nid: gift.nid,
                    postfix: isGiftCourse
                        ? gift.image.image_url_postfix
                        : gift.image_url_postfix,
                    fileName: isGiftCourse
                        ? gift.image.image_file_name
                        : gift.image_file_name,
                };
            });

        if (itemsToFetch.length === 0) return;

        const newThumbnails = await fetchBatchThumbnailUrls(itemsToFetch);

        setThumbnails((prev) => ({
            ...prev,
            ...newThumbnails,
        }));
    };

    let contentItemType: AppContentListItemType = "gift";

    const goBack = () => {
        resetGlobalGiftsCategoryStack(categoryId);
        if (!homeStack?.isHomeTrack) {
            navigate(
                linkProvider.react
                    .gifts()
                    .giftsGivenListing("gifts-given", { scrollTo: +scrollTo }),
            );
        } else {
            navigate(-1);
        }
    };

    const setSelectedTab = (tab: AllTabs) => {
        navigate(
            linkProvider.react
                .gifts()
                .giftsGivenContentListing(memberId, tab, categoryId, {
                    scrollTo: +scrollTo,
                }),
            { replace: true },
        );
    };

    return (
        <Layout
            paginationInfo={!loading && paginationInfo}
            onLastContentHit={() =>
                setPaginationInfo({
                    ...paginationInfo,
                    perPage: paginationInfo.perPage + 10,
                })
            }
        >
            <RootDiv className={className} {...rest}>
                <AppHeader
                    title={"Gifts"}
                    canGoBack
                    searchable={true}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    tabs={Tabs}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    showTabs={true}
                    searchPlaceholder={`Search ${selectedTab}s by title or description`}
                    onBackClick={() => {
                        goBack();
                    }}
                />

                {loaded &&
                    finalData?.map((g, idx) => (
                        <AppContentListItem
                            nid={g.nid}
                            type={contentItemType}
                            key={idx}
                            title={g.title}
                            skuId={g.sku_id}
                            description={g.description}
                            imageUrl={thumbnails && thumbnails[g.nid]}
                            isAllowClick
                            onListItemClick={() => {}}
                            releaseDate={g.sending_date}
                            isGiftGiven={true}
                        />
                    ))}

                {loading && (
                    <WrapperLoaderGrid item={true} xs={12} md={12}>
                        <AppCircularProgress />
                    </WrapperLoaderGrid>
                )}
                {loaded && !filteredGifts.length && (
                    <NoContentTypography align="center" variant="h6">
                        {messaging.common.noItemsFound}
                    </NoContentTypography>
                )}
            </RootDiv>
        </Layout>
    );
};

const useQueryParams = () => {
    const location = useLocation();
    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo,
        };
    }, [location?.search]);
};
